import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// Bundling the pdf.js worker locally (rather than the library's default
// cdnjs fallback) avoids depending on an external CDN at view time and
// guarantees the worker version always matches the installed pdfjs-dist.
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

const SCALE = 2;

function PdfPage({ pdfDoc, pageNumber, containerRef, onVisibilityChange }: {
  pdfDoc: PDFDocumentProxy;
  pageNumber: number;
  containerRef: RefObject<HTMLDivElement | null>;
  onVisibilityChange: (page: number, ratio: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const renderedRef = useRef(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const root = containerRef.current;
    if (!wrapper || !root) return;

    const renderPage = async () => {
      if (renderedRef.current) return;
      renderedRef.current = true;
      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: SCALE });
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');
      if (!canvas || !context) return;
      const devicePixelRatio = window.devicePixelRatio || 1;
      canvas.width = viewport.width * devicePixelRatio;
      canvas.height = viewport.height * devicePixelRatio;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      context.scale(devicePixelRatio, devicePixelRatio);
      await page.render({ canvasContext: context, canvas, viewport }).promise;
    };

    // rootMargin renders a page slightly before it scrolls into view, so
    // continuous scrolling doesn't show a blank canvas while it renders.
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        onVisibilityChange(pageNumber, entry.isIntersecting ? entry.intersectionRatio : 0);
        if (entry.isIntersecting) {
          renderPage();
        }
      });
    }, { root, rootMargin: '400px 0px', threshold: [0, 0.25, 0.5, 0.75, 1] });

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [pdfDoc, pageNumber, containerRef, onVisibilityChange]);

  return (
    <Box
      ref={wrapperRef}
      data-page-number={pageNumber}
      sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%' }} />
    </Box>
  );
}

export const PdfViewer = (props: { file: string }) => {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const visibilityByPage = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    let cancelled = false;

    import('pdfjs-dist').then(({ getDocument, GlobalWorkerOptions }) => {
      GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
      return getDocument({ url: props.file }).promise;
    }).then((doc) => {
      if (!cancelled) setPdfDoc(doc);
    }).catch((err) => {
      console.error('PdfViewer: failed to load document', props.file, err);
      if (!cancelled) setError("Le document PDF n'a pas pu etre charge.");
    });

    return () => { cancelled = true; };
  }, [props.file]);

  const handleVisibilityChange = useCallback((page: number, ratio: number) => {
    visibilityByPage.current.set(page, ratio);
    let bestPage = 0;
    let bestRatio = 0;
    visibilityByPage.current.forEach((r, p) => {
      if (r > bestRatio) {
        bestRatio = r;
        bestPage = p;
      }
    });
    if (bestPage > 0) setCurrentPage(bestPage);
  }, []);

  const goToPage = useCallback((page: number) => {
    if (!pdfDoc) return;
    const clamped = Math.min(Math.max(page, 1), pdfDoc.numPages);
    const target = containerRef.current?.querySelector<HTMLElement>(`[data-page-number="${clamped}"]`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pdfDoc]);

  // Focus the scroll container once the document is ready so Up/Down arrow
  // keys work right away, without requiring the user to click inside it first.
  useEffect(() => {
    if (pdfDoc) {
      containerRef.current?.focus();
    }
  }, [pdfDoc]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: 'white', height: '100%', width: '100%' }}>
      {!pdfDoc && !error && <CircularProgress color="inherit" size={32} />}
      {error && <Typography color="error.light">{error}</Typography>}
      {pdfDoc && (
        <Box
          ref={containerRef}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'PageDown') {
              event.preventDefault();
              goToPage(currentPage + 1);
            } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
              event.preventDefault();
              goToPage(currentPage - 1);
            }
          }}
          sx={{
            width: '95vw',
            maxWidth: 1600,
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            outline: 'none',
          }}
        >
          {Array.from({ length: pdfDoc.numPages }, (_, index) => index + 1).map((pageNumber) => (
            <PdfPage
              key={pageNumber}
              pdfDoc={pdfDoc}
              pageNumber={pageNumber}
              containerRef={containerRef}
              onVisibilityChange={handleVisibilityChange}
            />
          ))}
        </Box>
      )}
      {pdfDoc && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <IconButton
            aria-label="Page precedente"
            disabled={currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
            sx={{ color: 'white' }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2">
            {currentPage} / {pdfDoc.numPages}
          </Typography>
          <IconButton
            aria-label="Page suivante"
            disabled={currentPage === pdfDoc.numPages}
            onClick={() => goToPage(currentPage + 1)}
            sx={{ color: 'white' }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
